import { defineStore } from 'pinia'

import {
    exportLeadsCSVData,
    generateLeadsXLSXData,
    getLeadsXLSXStatuses,
    downloadLeadsXLSXData,
} from '@/api/leads-routes'

import { downloadFile } from '@/helpers/app-helper'

import { useDefaultStore } from '@/store/defaultStore'

export const useDownloadLeadsStore = defineStore('downloadLeadsStore', {
    state: () => ({
        downloadingLeads: false,
        downloadingLeadsStatus: null,
        leadsFilename: 'leads-export.csv',
        leadsSize: 0,
        leadsFileSlug: null,
        leadsProgress: 0,
        statuses: {
            inProgress: 'in_process',
            success: 'success',
            failed: 'failed',
        },
        timer: null,
    }),

    actions: {
        async goToExportLeadsCSVData(params) {
            const defaultStore = useDefaultStore()

            this.downloadingLeads = true
            this.downloadingLeadsStatus = this.statuses.inProgress
            this.leadsFilename = 'leads-export.csv'
            this.leadsSize = 0

            const res = await exportLeadsCSVData({ params, onDownloadProgress: this.onDownloadLeadsProgress })

            if (res?.data && res.data.success === false) {
                !!res.data.message && defaultStore.setErrorMessage({ message: res.data.message })
                this.downloadingLeadsStatus = this.statuses.failed
            } else if (res?.success === false) {
                !!res.message && defaultStore.setErrorMessage({ message: res.message })
                this.downloadingLeadsStatus = this.statuses.failed
            } else {
                downloadFile(res, 'csv')
                this.downloadingLeadsStatus = this.statuses.success
            }

            this.downloadingLeads = false
        },

        removeLeadsStatus() {
            this.downloadingLeadsStatus = null
        },

        onDownloadLeadsProgress(progressEvent) {
            if (progressEvent.loaded) {
                this.leadsSize = Math.round(progressEvent.loaded * 0.000001)
            }
        },

        async goToGenerateLeadsXLSXData(params) {
            const defaultStore = useDefaultStore()

            this.downloadingLeads = true
            this.downloadingLeadsStatus = this.statuses.inProgress
            this.leadsSize = 0

            const { success, payload, message } = await generateLeadsXLSXData(params)

            if (success) {
                this.leadsFilename = payload.name
                this.leadsFileSlug = payload.slug
                this.checkStatus(payload.status)
            } else if (message === 'Report generation is in progress') {
                this.goToCheckLeadsXLSXStatuses()
            } else {
                defaultStore.setErrorMessage({ message })
                this.downloadingLeads = false
                this.downloadingLeadsStatus = this.statuses.failed
                this.leadsFilename = 'leads-export.xlsx'
            }
        },

        checkStatus(status) {
            if (!status) {
                this.downloadingLeads = false
                this.downloadingLeadsStatus = null
                this.resetTimer()
                return
            }

            switch (status) {
            case this.statuses.inProgress:
                this.downloadingLeads = true
                this.downloadingLeadsStatus = this.statuses.inProgress
                this.setTimerForCheckLeadsXLSXStatus()
                break
            case this.statuses.success:
                this.downloadingLeadsStatus = this.statuses.success
                this.resetTimer()
                this.goToDownloadLeadsXLSX()
                break
            case this.statuses.failed:
                this.downloadingLeads = false
                this.downloadingLeadsStatus = this.statuses.failed
                this.resetTimer()
                break
            }
        },

        setTimerForCheckLeadsXLSXStatus() {
            this.resetTimer()

            this.timer = setInterval(() => {
                this.downloadingLeads = true
                this.goToCheckLeadsXLSXStatuses()
            }, 10000)
        },

        resetTimer() {
            clearInterval(this.timer)
        },

        async goToCheckLeadsXLSXStatuses() {
            const defaultStore = useDefaultStore()

            const { success, payload, message } = await getLeadsXLSXStatuses()

            if (success) {
                this.leadsFilename = payload.name
                this.leadsFileSlug = payload.slug
                this.checkStatus(payload.hasOwnProperty('status') ? payload.status : null)
            } else {
                defaultStore.setErrorMessage({ message })
                this.resetTimer()
                this.downloadingLeads = false
                this.downloadingLeadsStatus = this.statuses.failed
            }
        },

        async goToDownloadLeadsXLSX() {
            const defaultStore = useDefaultStore()

            this.downloadingLeads = true

            const res = await downloadLeadsXLSXData(this.leadsFileSlug)

            if (res?.data && res.data.success === false) {
                !!res.data.message && defaultStore.setErrorMessage({ message: res.data.message })
                this.downloadingLeadsStatus = this.statuses.failed
            } else if (res?.success === false) {
                !!res.message && defaultStore.setErrorMessage({ message: res.message })
                this.downloadingLeadsStatus = this.statuses.failed
            } else {
                this.downloadingLeadsStatus = this.statuses.success
                downloadFile(res, 'xlsx')
            }

            this.downloadingLeads = false
        },
    },
})
